import type { ErrorInfo } from 'react';
import { API_URL } from './api';

/**
 * TCR-1192 — Gửi lỗi render FE (bắt bởi `RouteErrorBoundary`) về BE
 * `POST /v1/client-errors`.
 *
 * Cố ý KHÔNG dùng instance `api` (axios) chuẩn của `src/services/api.ts`:
 * endpoint này không cần auth và phải sống sót qua lúc trang unload (user rời
 * trang ngay sau khi lỗi xảy ra) — `sendBeacon` giữ được request đó, còn
 * axios + interceptor refresh token phức tạp thì không, và có thể còn làm
 * chậm/chặn quá trình unload.
 */

const CLIENT_ERROR_ENDPOINT = `${API_URL}/client-errors`;

/** route + message giống hệt đã gửi trong vòng 60s gần nhất thì bỏ qua — chống spam khi lỗi lặp lại liên tục (vòng lặp render lỗi). */
const DEBOUNCE_WINDOW_MS = 60_000;

/** Module-level — KHÔNG dùng localStorage, không cần bền qua reload. */
const lastSentAt = new Map<string, number>();

export interface ReportClientErrorContext {
  tenant_id?: string;
  user_id?: string;
}

interface ClientErrorPayload {
  route: string;
  message: string;
  stack?: string;
  component_stack?: string;
  tenant_id?: string;
  user_id?: string;
  user_agent: string;
  occurred_at: string;
}

function shouldSend(key: string, now: number): boolean {
  const last = lastSentAt.get(key);
  if (last !== undefined && now - last < DEBOUNCE_WINDOW_MS) {
    return false;
  }
  lastSentAt.set(key, now);
  return true;
}

function sendPayload(payload: ClientErrorPayload): void {
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const accepted = navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, blob);
      if (accepted) return;
    }
  } catch {
    // Rơi xuống fallback fetch bên dưới.
  }

  try {
    void fetch(CLIENT_ERROR_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Nuốt lỗi — xem comment ở reportClientError.
    });
  } catch {
    // Nuốt lỗi — xem comment ở reportClientError.
  }
}

/**
 * KHÔNG BAO GIỜ throw — gọi từ `componentDidCatch`, một lỗi ở đây không được
 * làm hỏng trải nghiệm hơn nữa (route đã đang crash). Thất bại thì tối đa
 * `console.warn` 1 dòng.
 */
export function reportClientError(
  error: Error,
  info: Pick<ErrorInfo, 'componentStack'> | { componentStack?: string | null },
  context?: ReportClientErrorContext,
): void {
  try {
    const route = typeof window !== 'undefined' ? window.location.pathname : '';
    const message = error.message || String(error);
    const debounceKey = `${route}::${message}`;
    const now = Date.now();

    if (!shouldSend(debounceKey, now)) return;

    const payload: ClientErrorPayload = {
      route,
      message,
      stack: error.stack,
      component_stack: info.componentStack ?? undefined,
      tenant_id: context?.tenant_id,
      user_id: context?.user_id,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      occurred_at: new Date(now).toISOString(),
    };

    sendPayload(payload);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[reportClientError] gửi log lỗi thất bại', err);
  }
}
